import type { WindZoneGeometry, ZoneResult } from '../../gen/ejot/roofing/v1/types';
import {
  formatArea,
  formatCount,
  formatLength,
  formatQuantityWithUnit,
  formatUtilization,
} from '../../shared/format';
import { zoneFixingPresentation } from './fixingPresentation';

interface WindZoneInspectorProps {
  zones: readonly WindZoneGeometry[];
  results: readonly ZoneResult[];
  selectedZoneId?: string;
  onSelect: (zoneId: string | undefined) => void;
}

/**
 * Accessible inspection of backend wind-zone and fixing facts.
 *
 * The inspector never derives an engineering value. Geometry facts come from
 * WindZoneGeometry and calculation/fixing facts come from the matching
 * ZoneResult. Missing facts remain explicitly unavailable. ETA component
 * characteristic data is never relabelled as complete-system design capacity.
 */
export function WindZoneInspector({
  zones,
  results,
  selectedZoneId,
  onSelect,
}: WindZoneInspectorProps) {
  if (zones.length === 0) {
    return (
      <aside className="wind-zone-inspector" aria-label="Wind zone inspector">
        <p className="empty-hint">No authoritative backend wind-zone geometry is available.</p>
      </aside>
    );
  }

  const resultByZone = new Map(results.map((result) => [result.zone_id ?? '', result]));
  const selected = zones.find((zone) => zone.id === selectedZoneId) ?? zones[0];
  const result = resultByZone.get(selected?.id ?? '');
  const fixing = zoneFixingPresentation(result);

  return (
    <aside className="wind-zone-inspector" aria-label="Wind zone inspector">
      <div className="wind-zone-picker" role="group" aria-label="Select wind zone">
        {zones.map((zone) => (
          <button
            type="button"
            key={zone.id}
            className={`wind-zone-picker-button${zone.id === selected?.id ? ' selected' : ''}`}
            onClick={() => onSelect(zone.id)}
          >
            <span>Zone {zone.display_label || '—'}</span>
            <small>{formatArea(zone.area, 'Unavailable')}</small>
          </button>
        ))}
      </div>

      {selected && (
        <div className="wind-zone-detail" aria-live="polite">
          <div className="wind-zone-detail-heading">
            <strong>Zone {selected.display_label || '—'}</strong>
            <button type="button" className="text-button" onClick={() => onSelect(undefined)}>
              Clear
            </button>
          </div>

          <dl className="wind-zone-detail-grid">
            <Fact label="Zone ID" value={selected.id || 'Unavailable'} />
            <Fact label="Area" value={formatArea(selected.area, 'Unavailable')} />
            <Fact
              label="External coefficient"
              value={formatQuantityWithUnit(result?.external_pressure_coefficient, 'Unavailable')}
            />
            <Fact
              label="Characteristic pressure"
              value={formatQuantityWithUnit(result?.characteristic_pressure, 'Unavailable')}
            />
            <Fact
              label="Design pressure"
              value={formatQuantityWithUnit(result?.design_pressure, 'Unavailable')}
            />
            <Fact
              label={fixing.resistanceLabel}
              value={formatQuantityWithUnit(fixing.resistance, 'Unavailable')}
            />
            {!fixing.etaComponentMode && (
              <Fact label="Utilization" value={formatUtilization(fixing.utilization, 'Unavailable')} />
            )}
            <Fact
              label={fixing.densityLabel}
              value={formatQuantityWithUnit(fixing.theoreticalDensity, 'Unavailable')}
            />
            {!fixing.etaComponentMode && (
              <>
                <Fact
                  label="Installable density"
                  value={formatQuantityWithUnit(fixing.installableDensity, 'Unavailable')}
                />
                <Fact
                  label="Fixings per metre"
                  value={formatQuantityWithUnit(fixing.fixingsPerMetre, 'Unavailable')}
                />
                <Fact
                  label="Spacing in overlap"
                  value={formatLength(fixing.spacing, 'Unavailable')}
                />
              </>
            )}
            <Fact
              label="Fixings in zone"
              value={fixing.totalFixings === undefined ? 'Unavailable' : `${formatCount(fixing.totalFixings)} pcs`}
            />
          </dl>

          {!result && (
            <p className="field-note">
              Geometry is available from the backend. Calculation values remain unavailable until
              a matching backend calculation result exists.
            </p>
          )}

          {fixing.adjustmentReasons.length > 0 && (
            <div className="adjustment-reasons">
              <strong>{fixing.etaComponentMode ? 'Calculation basis' : 'Installable layout adjustments'}</strong>
              <ul>
                {fixing.adjustmentReasons.map((reason, index) => <li key={index}>{reason}</li>)}
              </ul>
            </div>
          )}

          {(result?.messages ?? []).length > 0 && (
            <ul className="wind-zone-message-list">
              {(result?.messages ?? []).map((message) => (
                <li key={`${message.code}-${message.affected_object_id ?? ''}`}>
                  <strong>{message.title || message.code}</strong>
                  {message.explanation ? ` — ${message.explanation}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </aside>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
